<script lang="ts" setup>
import Card from 'primevue/card'
import Button from 'primevue/button'
import InputSwitch from 'primevue/inputswitch'
import InputLabel from '@/components/inputs/InputLabel.vue'
import FormSection from '@/components/forms/FormSection.vue'
import FormTextArea from '@/components/inputs/FormTextArea.vue'
import FormInputText from '@/components/inputs/FormInputText.vue'
import SupplierAddressFields from '@/components/features/suppliers/SupplierAddressFields.vue'
import router from '@/router'
import { ref, onMounted, watch } from 'vue'
import { computed } from 'vue'
import { Icon } from '@iconify/vue'
import { customerSchema } from '@/schemas/customerSchema'
import { convertErrorsToVeeValidateFormat } from '@/utils'
import {type Supplier} from '@/types/supplier-types'
import { editSupplier, getSupplierById } from '@/api/suppliers'

const props = defineProps<{
  values: any
  addressFields: any[]
  type: 'add' | 'edit' | 'view'
  onSubmit: () => void
  setErrors: (errors: any) => void
  newAddress: (address: any) => void
  removeAddress: (index: number) => void
  showToast?: (type: 'success' | 'error', title: string, message: string) => void
}>()

const supplierId = router.currentRoute.value?.params?.id as string
const checked = ref<boolean | undefined>(props.values.isActive)
const supplier = ref<Supplier | null>(null)

const validateAddress = (): boolean => {
  const validation = customerSchema.pick({ addresses: true }).safeParse({
    addresses: props.values.addresses
  })
  if (!validation.success)
    props.setErrors(convertErrorsToVeeValidateFormat(validation.error.issues))

  return validation.success
}

const addNewAddress = () => {
  props.newAddress({})
}

const isViewPage = computed<boolean>(() => props.type === 'view')
const isAddPage = computed<boolean>(() => props.type === 'add')
const showRemoveButton = computed<boolean>(() => props.addressFields.length > 1)
const showAddressSection = computed<boolean>(() => props.addressFields.length > 0)

const updateSupplierStatus = async () => {
  if(!supplier.value) return;
  try {
    await editSupplier({...supplier.value, isActive: checked.value }, supplierId)
  }catch(err){
    console.error('Error updating supplier status:', err)
  }
}

onMounted(async () => {
  try {
    supplier.value = await getSupplierById(supplierId)
    if (supplier.value) {
      checked.value = supplier.value.isActive
    }
  } catch (error) {
    console.error('Error fetching supplier data:', error)
  }
})
watch(checked, () => {
  updateSupplierStatus()
})
</script>

<template>
  <form @submit.prevent="onSubmit" class="border-t border-neutral-200/50 !rounded-t-3xl">
    <Card>
      <template #content>
        <div class="">
          <FormSection
            title="Supplier Information"
            :description="`${type === 'view' ? '' : 'Enter the supplier details'}`"
          >
            <template #status v-if="!isAddPage">
              <div class="flex items-center gap-x-2 col-span-2">
                <InputLabel class="!mb-0">This supplier is currently {{ supplier?.isActive ? 'active' : 'inactive' }}</InputLabel>
                <InputSwitch v-model="checked" />
              </div>
            </template>

            <FormInputText
              :viewOnly="isViewPage"
              label="Supplier's Name"
              inputName="name"
              id="name"
            />

            <FormInputText
              :viewOnly="isViewPage"
              label="Contact Person"
              inputName="contactPerson"
              id="contactPerson"
            />

            <FormInputText
              :viewOnly="isViewPage"
              label="Contact's Landline Number"
              inputName="contactPersonPhoneNumber"
              id="contactPersonPhoneNumber"
            />

            <FormInputText
              :viewOnly="isViewPage"
              label="Contact's Mobile Number"
              inputName="contactPersonMobileNumber"
              id="contactPersonMobileNumber"
            />

            <FormInputText
              :viewOnly="isViewPage"
              label="Contact's Email Address"
              inputName="contactPersonEmail"
              id="contactPersonEmail"
            />

            <FormTextArea
              :viewOnly="isViewPage"
              label="Supplier's Description"
              inputName="supplierDescription"
              id="supplierDescription"
            />
          </FormSection>
          <FormSection
            v-if="showAddressSection"
            title="Supplier Address"
            description="Enter the supplier address details"
          >
            <template v-for="(addressField, index) in addressFields" :key="addressField.key">
              <SupplierAddressFields
                :expandedAddressView="isAddPage || Object.keys(addressField.value).length === 0"
                :validateAddress="validateAddress"
                :formPath="`addresses[${index}]`"
                :showRemoveButton="showRemoveButton"
                :addressValues="values?.addresses[index]"
                :removeAddress="() => removeAddress(index)"
                :type="type"
              />
            </template>
          </FormSection>
        </div>
        <Button
          v-ripple
          class="gap-x-2 w-full border-dashed py-4 mb-4"
          outlined
          type="button"
          @click="addNewAddress"
          v-if="!isViewPage"
        >
          <span>{{ showAddressSection ? 'Add Another Address' : 'Add Supplier Address' }} </span>
          <Icon icon="ic:baseline-plus" class="text-xl" />
        </Button>
      </template>
    </Card>
  </form>
</template>
<style></style>